import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Visual effects feature flags.
 *
 * Every motion effect on the site is a feature the visitor can toggle.
 * Defaults: everything ON — the motion is part of the brand. Visitors who
 * prefer calm (or whose OS requests reduced motion) can switch effects off;
 * their choice persists in localStorage.
 */
export interface EffectsState {
	/** Interactive particle network page background */
	particles: boolean
	/** Spinning / rainbow-shifting logo icons */
	logoMotion: boolean
	/** Particle data-flow animation between the logos */
	dataFlow: boolean
	/** 3D tilt + glare on the hero card */
	tilt: boolean
	/** Brightness pulse on CTA buttons */
	ctaPulse: boolean
	/** Typewriter animation in the hero terminal */
	typewriter: boolean
	/** Scroll-triggered reveals and animated skill bars */
	scrollReveal: boolean
	/** Cursor-following glow on section cards */
	cardGlow: boolean
}

export type EffectKey = keyof EffectsState

export const EFFECT_LABELS: Record<EffectKey, string> = {
	particles: 'Particle background',
	logoMotion: 'Logo animation',
	dataFlow: 'Data-flow particles',
	tilt: '3D tilt + glare',
	ctaPulse: 'Button pulse',
	typewriter: 'Terminal typewriter',
	scrollReveal: 'Scroll animations',
	cardGlow: 'Card hover glow'
}

const ALL_ON: EffectsState = {
	particles: true,
	logoMotion: true,
	dataFlow: true,
	tilt: true,
	ctaPulse: true,
	typewriter: true,
	scrollReveal: true,
	cardGlow: true
}

const ALL_OFF: EffectsState = {
	particles: false,
	logoMotion: false,
	dataFlow: false,
	tilt: false,
	ctaPulse: false,
	typewriter: false,
	scrollReveal: false,
	cardGlow: false
}

const STORAGE_KEY = 'effects-prefs'

const loadInitialState = (): EffectsState => {
	if (typeof window === 'undefined') return ALL_ON
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY)
		if (stored) {
			return { ...ALL_ON, ...JSON.parse(stored) as Partial<EffectsState> }
		}
	} catch {
		// ignore storage/parse errors, fall through to defaults
	}
	// No stored preference: respect the OS-level reduced-motion signal,
	// otherwise everything is on.
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return ALL_OFF
	}
	return ALL_ON
}

type EffectsContextType = {
	effects: EffectsState
	/** True if at least one effect is enabled */
	anyEnabled: boolean
	toggleEffect: (key: EffectKey) => void
	setAll: (enabled: boolean) => void
}

const EffectsContext = createContext<EffectsContextType | undefined>(undefined)

export const EffectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [effects, setEffects] = useState<EffectsState>(loadInitialState)

	useEffect(() => {
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(effects))
		} catch {
			// storage unavailable — non-fatal
		}
	}, [effects])

	const value = useMemo<EffectsContextType>(() => ({
		effects,
		anyEnabled: Object.values(effects).some(Boolean),
		toggleEffect: (key: EffectKey) =>
			setEffects(prev => ({ ...prev, [key]: !prev[key] })),
		setAll: (enabled: boolean) => setEffects(enabled ? ALL_ON : ALL_OFF)
	}), [effects])

	return <EffectsContext.Provider value={value}>{children}</EffectsContext.Provider>
}

export const useEffects = (): EffectsContextType => {
	const context = useContext(EffectsContext)
	if (context === undefined) {
		throw new Error('useEffects must be used within an EffectsProvider')
	}
	return context
}
